import React, { useState, useEffect, useReducer } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Grid,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
    minHeight: 460,
  },
  tabContent: {
    padding: theme.spacing(2),
  },
  addForm: {
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f9f9f9",
  },
  listItem: {
    borderRadius: 8,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  categoryDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
    verticalAlign: "middle",
    flexShrink: 0,
  },
  editRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    width: "100%",
    flexWrap: "wrap",
  },
  defaultChip: {
    height: 20,
    fontSize: "0.68rem",
    fontWeight: 700,
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
  },
  emptyMsg: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: "#9e9e9e",
  },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

// ─── reducers ──────────────────────────────────────────────────────────────

const listReducer = (state, action) => {
  if (action.type === "SET")    return action.payload;
  if (action.type === "ADD")    return [...state, action.payload];
  if (action.type === "UPDATE") return state.map(i => i.id === action.payload.id ? action.payload : i);
  if (action.type === "DELETE") return state.filter(i => i.id !== action.payload);
  return state;
};

// ─── Palete de cores padrão ──────────────────────────────────────────────────

const COLOR_OPTIONS = [
  "#4CAF50", "#2196F3", "#9C27B0", "#FF9800", "#F44336",
  "#00BCD4", "#795548", "#607D8B", "#E91E63", "#FFC107",
  "#9E9E9E", "#3F51B5",
];

const ColorPicker = ({ value, onChange }) => {
  const classes = useStyles();
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {COLOR_OPTIONS.map(c => (
        <div
          key={c}
          onClick={() => onChange(c)}
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            backgroundColor: c,
            cursor: "pointer",
            border: value === c ? "3px solid #000" : "2px solid transparent",
            boxSizing: "border-box",
            transition: "border 0.1s",
          }}
        />
      ))}
    </div>
  );
};

// ─── Aba Categorias ──────────────────────────────────────────────────────────

const emptyCategory = { name: "", type: "income", color: "#4CAF50" };

const CategoriesTab = () => {
  const classes = useStyles();
  const [categories, dispatch] = useReducer(listReducer, []);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyCategory);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/transaction-categories")
      .then(({ data }) => dispatch({ type: "SET", payload: data }))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome da categoria."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/transaction-categories", form);
      dispatch({ type: "ADD", payload: data });
      setForm(emptyCategory);
      toast.success("Categoria criada.");
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, color: cat.color });
  };

  const handleUpdate = async (id) => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/transaction-categories/${id}`, editForm);
      dispatch({ type: "UPDATE", payload: data });
      setEditingId(null);
      toast.success("Categoria atualizada.");
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/transaction-categories/${id}`);
      dispatch({ type: "DELETE", payload: id });
      toast.success("Categoria excluída.");
    } catch (err) { toastError(err); }
    finally { setDeletingId(null); }
  };

  const income  = categories.filter(c => c.type === "income");
  const expense = categories.filter(c => c.type === "expense");

  const renderList = (items) => items.map(cat => (
    <ListItem key={cat.id} className={classes.listItem} dense>
      {editingId === cat.id ? (
        <div className={classes.editRow}>
          <TextField
            size="small"
            value={editForm.name}
            onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
            style={{ flex: 1, minWidth: 120 }}
          />
          <ColorPicker value={editForm.color} onChange={c => setEditForm(p => ({ ...p, color: c }))} />
          <IconButton size="small" onClick={() => handleUpdate(cat.id)} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" style={{ color: "#2e7d32" }} />}
          </IconButton>
          <IconButton size="small" onClick={() => setEditingId(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ) : (
        <>
          <span className={classes.categoryDot} style={{ backgroundColor: cat.color }} />
          <ListItemText primary={cat.name} />
          <ListItemSecondaryAction>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => startEdit(cat)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => handleDelete(cat.id)} disabled={deletingId === cat.id}>
                {deletingId === cat.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
        </>
      )}
    </ListItem>
  ));

  return (
    <div className={classes.tabContent}>
      {/* Formulário de nova categoria */}
      <Paper className={classes.addForm} elevation={0}>
        <Typography className={classes.sectionLabel}>Nova Categoria</Typography>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12} sm={5}>
            <TextField
              label="Nome"
              size="small"
              fullWidth
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              inputProps={{ maxLength: 60 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <MenuItem value="income">Receita</MenuItem>
                <MenuItem value="expense">Despesa</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <ColorPicker value={form.color} onChange={c => setForm(p => ({ ...p, color: c }))} />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAdd}
              disabled={saving}
              style={{ minWidth: 0, padding: "6px 10px" }}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon fontSize="small" />}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}><CircularProgress /></div>
      ) : (
        <>
          <Typography className={classes.sectionLabel}>Receitas ({income.length})</Typography>
          {income.length === 0
            ? <Typography className={classes.emptyMsg} style={{ padding: "8px 0 16px" }}>Nenhuma categoria de receita.</Typography>
            : <List dense disablePadding>{renderList(income)}</List>
          }

          <Divider style={{ margin: "16px 0" }} />

          <Typography className={classes.sectionLabel}>Despesas ({expense.length})</Typography>
          {expense.length === 0
            ? <Typography className={classes.emptyMsg} style={{ padding: "8px 0" }}>Nenhuma categoria de despesa.</Typography>
            : <List dense disablePadding>{renderList(expense)}</List>
          }
        </>
      )}
    </div>
  );
};

// ─── Aba Formas de Pagamento ──────────────────────────────────────────────────

const PaymentMethodsTab = () => {
  const classes = useStyles();
  const [methods, dispatch] = useReducer(listReducer, []);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/payment-methods")
      .then(({ data }) => dispatch({ type: "SET", payload: data }))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error("Informe o nome."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payment-methods", { name: newName.trim() });
      dispatch({ type: "ADD", payload: data });
      setNewName("");
      toast.success("Forma de pagamento criada.");
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/payment-methods/${id}`, { name: editName.trim() });
      dispatch({ type: "UPDATE", payload: data });
      setEditingId(null);
      toast.success("Atualizado.");
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const handleToggleDefault = async (method) => {
    setTogglingId(method.id);
    try {
      const { data } = await api.put(`/payment-methods/${method.id}`, {
        name: method.name,
        isDefault: !method.isDefault
      });
      dispatch({ type: "UPDATE", payload: data });
      // limpa isDefault dos outros localmente
      if (data.isDefault) {
        methods.filter(m => m.id !== method.id && m.isDefault).forEach(m => {
          dispatch({ type: "UPDATE", payload: { ...m, isDefault: false } });
        });
      }
    } catch (err) { toastError(err); }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/payment-methods/${id}`);
      dispatch({ type: "DELETE", payload: id });
      toast.success("Removido.");
    } catch (err) { toastError(err); }
    finally { setDeletingId(null); }
  };

  return (
    <div className={classes.tabContent}>
      {/* Formulário de nova forma */}
      <Paper className={classes.addForm} elevation={0}>
        <Typography className={classes.sectionLabel}>Nova Forma de Pagamento</Typography>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <TextField
            label="Nome"
            size="small"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            style={{ flex: 1 }}
            inputProps={{ maxLength: 60 }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAdd}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
          >
            Adicionar
          </Button>
        </div>
      </Paper>

      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}><CircularProgress /></div>
      ) : methods.length === 0 ? (
        <Typography className={classes.emptyMsg}>Nenhuma forma de pagamento cadastrada.</Typography>
      ) : (
        <List dense disablePadding>
          {methods.map(method => (
            <ListItem key={method.id} className={classes.listItem} dense>
              {editingId === method.id ? (
                <div className={classes.editRow}>
                  <TextField
                    size="small"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    style={{ flex: 1 }}
                    onKeyDown={e => e.key === "Enter" && handleUpdate(method.id)}
                  />
                  <IconButton size="small" onClick={() => handleUpdate(method.id)} disabled={saving}>
                    {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" style={{ color: "#2e7d32" }} />}
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingId(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ) : (
                <>
                  <ListItemText
                    primary={
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {method.name}
                        {method.isDefault && (
                          <Chip label="Padrão" size="small" className={classes.defaultChip} />
                        )}
                      </span>
                    }
                  />
                  <ListItemSecondaryAction style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Tooltip title={method.isDefault ? "Remover padrão" : "Definir como padrão"}>
                      <span>
                        <Switch
                          size="small"
                          checked={!!method.isDefault}
                          onChange={() => handleToggleDefault(method)}
                          disabled={togglingId === method.id}
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => { setEditingId(method.id); setEditName(method.name); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleDelete(method.id)} disabled={deletingId === method.id}>
                        {deletingId === method.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </>
              )}
            </ListItem>
          ))}
        </List>
      )}
    </div>
  );
};

// ─── Dialog principal ─────────────────────────────────────────────────────────

const FinanceiroConfigDialog = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Configurações Financeiras</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginTop: 8 }}
        >
          <Tab label="Categorias" />
          <Tab label="Formas de Pagamento" />
        </Tabs>
      </DialogTitle>

      <DialogContent className={useStyles().dialogContent} dividers>
        {tab === 0 && <CategoriesTab />}
        {tab === 1 && <PaymentMethodsTab />}
      </DialogContent>
    </Dialog>
  );
};

export default FinanceiroConfigDialog;
